/**
 * 📡 Station Dashboard - Cetalingua FM
 *
 * Main dashboard for station users with 5 tabs (Noise added 8 Oct 2026):
 * 1. Station Info - Name, location, species
 * 2. Upload - Upload acoustic data with metadata
 * 3. Results - Analytics, detection heatmap, listeners
 * 4. Settings - Plan, storage, billing
 *
 * UPDATES:
 * ✅ CONSOLIDATED: Single station doc at stations/{stationId} (e.g. stations/500)
 * ✅ FIXED: Transaction-safe stationId assignment (no race conditions)
 * ✅ FIXED: Migrates legacy stations/{uid} docs to stations/{stationId}
 * ✅ FIXED: Writes stationId to users/{uid} for lookup
 *
 * Station IDs start from 500
 */

import React, { useState, useEffect } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, runTransaction } from 'firebase/firestore';
import { db } from '../../firebase-config';
import StationInfo from './tabs/StationInfo';
import StationUpload from './tabs/StationUpload';
import StationResults from './tabs/StationResults';
import StationSettings from './tabs/StationSettings';
import StationNoise from './tabs/StationNoise';
import './StationDashboard.css';

const TABS = [
  { id: 'info', label: 'Station Info', icon: '📡' },
  { id: 'upload', label: 'Upload', icon: '📤' },
  { id: 'results', label: 'Results', icon: '📊' },
  { id: 'noise', label: 'Noise', icon: '🔊' },
  { id: 'settings', label: 'Settings', icon: '⚙️' },
];

const StationDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [stationData, setStationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();

  useEffect(() => {
    loadStationData();
  }, [user]);

  /**
   * ✅ CONSOLIDATED LOAD FLOW:
   * 1. Read users/{uid} for stationId
   * 2. If stationId exists → read stations/{stationId}
   * 3. If no stationId → check legacy stations/{uid} and migrate
   * 4. If nothing found → assign new stationId and create station doc
   */
  const loadStationData = async () => {
    setLoading(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.exists() ? userDoc.data() : {};

      let stationId = userData.stationId || null;
      let stationDataResult = null;

      // ── Path A: User already has stationId ──
      if (stationId) {
        const stationRef = doc(db, 'stations', String(stationId));
        const stationDoc = await getDoc(stationRef);

        if (stationDoc.exists()) {
          stationDataResult = stationDoc.data();
        } else {
          // stationId on user doc but no station doc — create it
          stationDataResult = await createStationDoc(stationId, user, userData);
        }
      }

      // ── Path B: No stationId on user doc — check for legacy stations/{uid} ──
      if (!stationId) {
        const legacyRef = doc(db, 'stations', user.uid);
        const legacyDoc = await getDoc(legacyRef);

        if (legacyDoc.exists()) {
          const legacyData = legacyDoc.data();

          if (legacyData.stationId) {
            // Legacy doc has a stationId — migrate to stations/{stationId}
            stationId = legacyData.stationId;
            stationDataResult = await migrateLegacyStation(stationId, legacyData, user, userData);
          } else {
            // Legacy doc exists but no stationId — assign one and migrate
            stationId = await assignStationId();
            legacyData.stationId = stationId;
            stationDataResult = await migrateLegacyStation(stationId, legacyData, user, userData);
          }
        } else {
          // No legacy doc at all — fresh station
          stationId = await assignStationId();
          stationDataResult = await createStationDoc(stationId, user, userData);
        }

        // Write stationId to user doc
        await setDoc(userRef, { stationId: stationId }, { merge: true });
      }

      setStationData(stationDataResult);
    } catch (error) {
      console.error('Error loading station:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * ✅ TRANSACTION-SAFE: Assign next available station ID (starting from 500)
   * Uses counters/stations doc to avoid race conditions
   */
  const assignStationId = async () => {
    const counterRef = doc(db, 'counters', 'stations');
    const stationId = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      let nextId = 500;
      if (counterDoc.exists()) {
        nextId = counterDoc.data().nextId || 500;
      }
      transaction.set(counterRef, { nextId: nextId + 1 });
      return nextId;
    });

    console.log(`📡 Assigned new stationId: ${stationId}`);
    return stationId;
  };

  /**
   * Create a fresh station doc at stations/{stationId}
   */
  const createStationDoc = async (stationId, user, userData) => {
    const stationIdStr = String(stationId);
    const stationRef = doc(db, 'stations', stationIdStr);

    const newStation = {
      stationId: stationId,
      firebaseUid: user.uid,
      email: user.email,
      name: userData.companyName || '',
      description: userData.description || '',
      lat: 0,
      lng: 0,
      city: userData.location?.city || '',
      country: userData.location?.country || '',
      state: userData.location?.state || '',
      species: [],
      isActive: false,
      isOnline: false,
      status: 'active',
      followerCount: 0,
      subscriptionStatus: 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    await setDoc(stationRef, newStation);
    console.log(`📡 Created station doc: stations/${stationIdStr}`);
    return newStation;
  };

  /**
   * ✅ Migrate legacy stations/{uid} data into stations/{stationId}
   * Merges with any existing data at stations/{stationId} (e.g. from uploads)
   */
  const migrateLegacyStation = async (stationId, legacyData, user, userData) => {
    const stationIdStr = String(stationId);
    const stationRef = doc(db, 'stations', stationIdStr);

    // Check if stations/{stationId} already has data (from StationUpload)
    const existingDoc = await getDoc(stationRef);
    const existingData = existingDoc.exists() ? existingDoc.data() : {};

    // Merge: legacy data provides name/description/coords,
    // existing data provides isOnline/lastBroadcastDate from uploads
    const mergedData = {
      ...legacyData,
      ...existingData,
      // Ensure critical fields are set correctly
      stationId: stationId,
      firebaseUid: user.uid,
      email: user.email || legacyData.email || '',
      name: legacyData.name || existingData.name || userData.companyName || '',
      description: legacyData.description || existingData.description || '',
      // ✅ Map latitude/longitude → lat/lng, prefer whichever exists
      lat: legacyData.lat || parseFloat(legacyData.latitude) || existingData.lat || 0,
      lng: legacyData.lng || legacyData.lon || parseFloat(legacyData.longitude) || existingData.lng || existingData.lon || 0,
      city: legacyData.city || existingData.city || userData.location?.city || '',
      country: legacyData.country || existingData.country || userData.location?.country || '',
      updatedAt: new Date().toISOString(),
    };

    // Clean up old field names that shouldn't be in the doc
    delete mergedData.latitude;
    delete mergedData.longitude;
    delete mergedData.lon;

    await setDoc(stationRef, mergedData, { merge: true });
    console.log(`📡 Migrated legacy station → stations/${stationIdStr}`);

    return mergedData;
  };

  const handleStationUpdate = () => {
    loadStationData();
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Failed to log out');
    }
  };

  if (loading) {
    return (
      <div className="station-dashboard">
        <div className="dashboard-loading">
          <div className="loading-icon">📡</div>
          <p>Loading station...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="station-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1>📡 Station Dashboard</h1>
          <p>Manage your hydrophone station</p>
        </div>
        <div className="header-right">
          <div className="station-chip">
            <span className="chip-label">Station ID</span>
            <span className="chip-value">{stationData?.stationId || '—'}</span>
          </div>
          
          <button className="logout-btn" onClick={handleLogout} title="Log Out">
            <span style={{ fontSize: '18px' }}>🚪</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <nav className="dashboard-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </nav>

      {/* Tab Content */}
      <main className="dashboard-content">
        {activeTab === 'info' && (
          <StationInfo
            user={user}
            stationData={stationData}
            onUpdate={handleStationUpdate}
          />
        )}
        {activeTab === 'upload' && (
          <StationUpload
            user={user}
            stationData={stationData}
          />
        )}
        {activeTab === 'results' && (
          <StationResults
            user={user}
            stationData={stationData}
          />
        )}
        {activeTab === 'noise' && <StationNoise stationData={stationData} />}
        {activeTab === 'settings' && (
          <StationSettings
            user={user}
            stationData={stationData}
          />
        )}
      </main>
    </div>
  );
};

export default StationDashboard;
